import React, { useState, useMemo, useEffect } from 'react';
import { Modal, message, Spin, Checkbox } from 'antd';
import { Event, TServerCalendarAppointmentEventDetail } from 'types/calendar';
import { TEligibleProvider, TTimeslot } from 'types/group';
import { executeReassign } from 'api/group';
import useReassignScenarios from 'hooks/group/useReassignScenarios';
import dayjs from 'utils/dayjs';
import s from './s.module.less';

type Props = {
    open: boolean;
    event?: Event;
    appointmentId?: number;
    appointmentDetail?: TServerCalendarAppointmentEventDetail;
    patientName?: string;
    timezone?: string;
    onCancel: () => void;
    onSuccess?: () => void;
};

const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const formatSlotTime = (slot: TTimeslot, tz?: string): string => {
    if (!slot.startAt || !slot.endAt) return '';
    const start = tz ? dayjs(slot.startAt).tz(tz) : dayjs(slot.startAt);
    const end = tz ? dayjs(slot.endAt).tz(tz) : dayjs(slot.endAt);
    return `${start.format('h:mm')} – ${end.format('h:mm A')}`;
};

const formatBadgeLabel = (provider: TEligibleProvider, tz?: string): string => {
    if (provider.available) return 'Available at the visit time';
    const firstSlot = provider.alternativeSlots?.[0];
    if (!firstSlot?.startAt) return 'Not available';
    const start = tz ? dayjs(firstSlot.startAt).tz(tz) : dayjs(firstSlot.startAt);
    const isToday = start.isSame(dayjs(), 'day');
    if (isToday) return `Today, ${start.format('h:mm A')}`;
    return start.format('ddd, MMM D · h:mm A');
};

const groupSlotsByDate = (slots: TTimeslot[], tz?: string): Record<string, TTimeslot[]> => {
    const groups: Record<string, TTimeslot[]> = {};
    slots.forEach((slot) => {
        if (!slot.startAt) return;
        const d = tz ? dayjs(slot.startAt).tz(tz) : dayjs(slot.startAt);
        const key = d.format('YYYY-MM-DD');
        if (!groups[key]) groups[key] = [];
        groups[key].push(slot);
    });
    return groups;
};

const ReassignAppointmentModal: React.FC<Props> = ({
    open,
    event,
    appointmentId,
    appointmentDetail,
    patientName,
    timezone,
    onCancel,
    onSuccess,
}) => {
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<TTimeslot | null>(null);
    const [timeslotProvider, setTimeslotProvider] = useState<TEligibleProvider | null>(null);
    const [pendingSlot, setPendingSlot] = useState<TTimeslot | null>(null);
    const [confirmedSlots, setConfirmedSlots] = useState<Record<number, TTimeslot>>({});
    const [submitting, setSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [useNewPrice, setUseNewPrice] = useState(true);
    const [notifyPatient, setNotifyPatient] = useState(true);
    const [notifyNewProvider, setNotifyNewProvider] = useState(true);
    const [notifyOriginalProvider, setNotifyOriginalProvider] = useState(true);

    const [scenarioData, loadingScenario] = useReassignScenarios(open ? appointmentId : undefined);

    const availableProviders = scenarioData?.availableProviders || [];
    const unavailableProviders = scenarioData?.unavailableProviders || [];
    const scenarioTz = scenarioData?.tz || timezone;
    const isCashPay = scenarioData?.payType === 'CASH_PAY';

    const appointmentTitle = appointmentDetail?.appointmentName || event?.summary || 'Appointment';
    const appointmentDate = event?.start?.dateTime
        ? dayjs(event.start.dateTime).tz(scenarioTz).format('ddd, MMM D, YYYY')
        : '';
    const appointmentTime =
        event?.start?.dateTime && event?.end?.dateTime
            ? `${dayjs(event.start.dateTime).tz(scenarioTz).format('h:mm')} – ${dayjs(event.end.dateTime).tz(scenarioTz).format('h:mm A')}`
            : '';
    const currentProviderName =
        appointmentDetail?.providerFirstName && appointmentDetail?.providerLastName
            ? `${appointmentDetail.providerFirstName} ${appointmentDetail.providerLastName}`
            : '';

    const handleSelectAvailable = (provider: TEligibleProvider) => {
        setSelectedProviderId(provider.providerId);
        setSelectedSlot(null);
        setTimeslotProvider(null);
    };

    const handleSelectUnavailable = (provider: TEligibleProvider) => {
        if (!provider.alternativeSlots?.length) return;
        setTimeslotProvider(provider);
        const firstSlot = provider.alternativeSlots[0];
        setPendingSlot(firstSlot);
    };

    useEffect(() => {
        if (!timeslotProvider) {
            setPendingSlot(null);
        }
    }, [timeslotProvider]);

    const handleConfirmTimeslot = () => {
        if (!timeslotProvider || !pendingSlot) return;
        setSelectedProviderId(timeslotProvider.providerId);
        setSelectedSlot(pendingSlot);
        setConfirmedSlots((prev) => ({ ...prev, [timeslotProvider.providerId]: pendingSlot }));
        setTimeslotProvider(null);
    };

    const defaultSlot: TTimeslot | null = useMemo(() => {
        const startAt = event?.start?.dateTime;
        const endAt = event?.end?.dateTime;
        if (!startAt || !endAt) return null;
        return { startAt, endAt };
    }, [event]);

    const handleConfirm = () => {
        if (!selectedProviderId || !appointmentId) return;
        setUseNewPrice(true);
        setNotifyPatient(true);
        setNotifyNewProvider(true);
        setNotifyOriginalProvider(true);
        setShowConfirmModal(true);
    };

    const selectedProvider = useMemo(() => {
        if (!selectedProviderId) return null;
        const allProviders = [...availableProviders, ...unavailableProviders];
        return allProviders.find((p) => p.providerId === selectedProviderId) || null;
    }, [selectedProviderId, availableProviders, unavailableProviders]);

    const showPriceSection = isCashPay && selectedProvider?.priceCompare && selectedProvider.priceCompare !== 'SAME';

    const getPriceDiffAction = (): string => {
        if (!showPriceSection) return 'NONE';
        if (!useNewPrice) return 'NONE';
        if (selectedProvider?.priceCompare === 'MORE') return 'CHARGE_PATIENT';
        if (selectedProvider?.priceCompare === 'LESS') return 'REFUND_PATIENT';
        return 'NONE';
    };

    const handleReassign = async () => {
        const slot = selectedSlot || defaultSlot;
        if (!selectedProviderId || !appointmentId || !slot?.startAt || !slot?.endAt) return;
        setSubmitting(true);
        try {
            const newStartAt = slot.startAt?.endsWith('Z') ? slot.startAt : `${slot.startAt}Z`;
            const newEndAt = slot.endAt?.endsWith('Z') ? slot.endAt : `${slot.endAt}Z`;
            const result = await executeReassign(appointmentId, {
                newProviderId: selectedProviderId,
                newStartAt,
                newEndAt,
                notifyNewProvider,
                notifyOriginalProvider,
                notifyPatient,
                priceDiffAction: getPriceDiffAction(),
            });
            if (!result.error) {
                message.success(`Visit reassigned to ${selectedProvider?.firstName} ${selectedProvider?.lastName}.`);
                setSelectedProviderId(null);
                setSelectedSlot(null);
                setConfirmedSlots({});
                setShowConfirmModal(false);
                onSuccess?.();
            } else {
                message.error('Failed to reassign visit. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setSelectedProviderId(null);
        setSelectedSlot(null);
        setTimeslotProvider(null);
        setConfirmedSlots({});
        setShowConfirmModal(false);
        onCancel();
    };

    const handleSkip = () => {
        onCancel();
    };

    const renderPriceBadge = (provider: TEligibleProvider) => {
        if (!isCashPay) return null;
        const { priceCompare, newServicePriceCents } = provider;
        const originalCents = scenarioData?.originalServicePriceCents;
        if (!priceCompare) return null;

        let label = '';
        let cls = '';
        if (priceCompare === 'SAME') {
            label = 'Same price';
            cls = s.priceSame;
        } else if (priceCompare === 'MORE') {
            const diff = (newServicePriceCents || 0) - (originalCents || 0);
            label = `$${Math.round(diff / 100)} more`;
            cls = s.priceHigher;
        } else if (priceCompare === 'LESS') {
            const diff = (originalCents || 0) - (newServicePriceCents || 0);
            label = `$${Math.round(diff / 100)} less`;
            cls = s.priceLower;
        }
        if (!label) return null;
        return <span className={`${s.priceBadge} ${cls}`}>{label}</span>;
    };

    const getAvailBadge = (provider: TEligibleProvider) => {
        const confirmed = confirmedSlots[provider.providerId];
        if (confirmed?.startAt) {
            const start = scenarioTz ? dayjs(confirmed.startAt).tz(scenarioTz) : dayjs(confirmed.startAt);
            const isToday = start.isSame(dayjs(), 'day');
            const label = isToday ? `Today, ${start.format('h:mm A')}` : start.format('ddd, MMM D · h:mm A');
            return <span className={`${s.availBadge} ${s.available}`}>{label}</span>;
        }

        const badgeText = formatBadgeLabel(provider, scenarioTz);
        if (provider.available) {
            return <span className={`${s.availBadge} ${s.available}`}>{badgeText}</span>;
        }
        const firstSlot = provider.alternativeSlots?.[0];
        if (!firstSlot) return null;
        const isToday = firstSlot.startAt && dayjs(firstSlot.startAt).isSame(dayjs(), 'day');
        return <span className={`${s.availBadge} ${isToday ? s.laterToday : s.anotherDay}`}>{badgeText}</span>;
    };

    const renderProviderCard = (provider: TEligibleProvider, isAvailable: boolean) => {
        const isSelected = selectedProviderId === provider.providerId;
        const hasSlots = !!provider.alternativeSlots?.length;
        return (
            <div
                key={provider.providerId}
                className={`${s.providerCard} ${isSelected ? s.providerCardSelected : ''}`}
                onClick={() => (isAvailable ? handleSelectAvailable(provider) : handleSelectUnavailable(provider))}
            >
                <div className={s.providerAvatar}>
                    {provider.avatar ? (
                        <img src={provider.avatar} alt="" className={s.providerAvatarImg} />
                    ) : (
                        getInitials(provider.firstName, provider.lastName)
                    )}
                </div>
                <div className={s.providerInfo}>
                    <div className={s.providerName}>
                        {provider.firstName} {provider.lastName}
                    </div>
                    <div className={s.providerCred}>
                        {[provider.credential, provider.specialty].filter(Boolean).join(' · ')}
                    </div>
                    {provider.state && <div className={s.providerLicenses}>Licensed in: {provider.state}</div>}
                    {renderPriceBadge(provider)}
                </div>
                <div className={s.providerRight}>
                    {getAvailBadge(provider)}
                    {(isAvailable || hasSlots) && (
                        <button
                            type="button"
                            className={`${s.selectBtn} ${isSelected ? s.selectBtnSelected : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isAvailable) {
                                    handleSelectAvailable(provider);
                                } else {
                                    handleSelectUnavailable(provider);
                                }
                            }}
                        >
                            {isSelected ? '✓ Selected' : 'Select'}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const timeslotGroups = useMemo(() => {
        if (!timeslotProvider?.alternativeSlots) return {};
        return groupSlotsByDate(timeslotProvider.alternativeSlots, scenarioTz);
    }, [timeslotProvider, scenarioTz]);

    return (
        <>
            <Modal
                title={null}
                open={open}
                onCancel={handleCancel}
                footer={null}
                width={560}
                destroyOnClose
                className={s.reassignModal}
            >
                <div className={s.header}>
                    <h2 className={s.title}>Reassign visit</h2>
                    <div className={s.appointmentContext}>
                        <div className={s.appointmentTitle}>{appointmentTitle}</div>
                        <div className={s.appointmentMeta}>
                            {appointmentDate && <span>📅 {appointmentDate}</span>}
                            {appointmentTime && (
                                <span>
                                    🕐 {appointmentTime} {scenarioTz}
                                </span>
                            )}
                        </div>
                        {currentProviderName && (
                            <div className={s.appointmentCurrentProvider}>
                                Currently: <strong>{currentProviderName}</strong>
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.body}>
                    {loadingScenario ? (
                        <div className={s.loadingWrap}>
                            <Spin />
                        </div>
                    ) : (
                        <>
                            {scenarioData?.blockReason && (
                                <div className={s.calloutWarning}>
                                    <svg className={s.calloutWarningIcon} viewBox="0 0 20 20" fill="currentColor">
                                        <path
                                            fillRule="evenodd"
                                            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className={s.calloutWarningText}>{scenarioData.blockReason}</span>
                                </div>
                            )}

                            {availableProviders.length > 0 && (
                                <div>
                                    <div className={s.sectionLabel}>Available at this time</div>
                                    {availableProviders.map((p) => renderProviderCard(p, true))}
                                </div>
                            )}

                            {unavailableProviders.length > 0 && (
                                <div>
                                    {availableProviders.length > 0 && <div className={s.divider} />}
                                    <div className={s.sectionLabel}>Not available at this time</div>
                                    <div className={s.calloutWarning}>
                                        <svg className={s.calloutWarningIcon} viewBox="0 0 20 20" fill="currentColor">
                                            <path
                                                fillRule="evenodd"
                                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <span className={s.calloutWarningText}>
                                            These providers are not available at the visit time. Selecting them will
                                            allow you to pick an available time slot.
                                        </span>
                                    </div>
                                    {unavailableProviders.map((p) => renderProviderCard(p, false))}
                                </div>
                            )}

                            {!loadingScenario &&
                                availableProviders.length === 0 &&
                                unavailableProviders.length === 0 && (
                                    <div className={s.emptyState}>
                                        <div className={s.emptyStateTitle}>No matching provider found</div>
                                        <div className={s.emptyStateDesc}>
                                            None of your providers hold the license required to treat this patient in
                                            their state.
                                        </div>
                                    </div>
                                )}
                        </>
                    )}
                </div>

                <div className={s.footer}>
                    <button
                        type="button"
                        className={s.confirmBtn}
                        disabled={!selectedProviderId || submitting}
                        onClick={handleConfirm}
                    >
                        {submitting ? 'Reassigning…' : 'Confirm reassignment'}
                    </button>
                    <button type="button" className={s.skipBtn} onClick={handleSkip}>
                        Skip assignment
                    </button>
                </div>
            </Modal>

            <Modal
                title={null}
                open={!!timeslotProvider}
                onCancel={() => setTimeslotProvider(null)}
                footer={null}
                width={420}
                destroyOnClose
                className={s.timeslotModal}
                zIndex={1010}
            >
                <div className={s.tsHeader}>
                    <div className={s.tsTitle}>Select a time slot</div>
                    <div className={s.tsSub}>
                        Choose an available time for {timeslotProvider?.firstName} {timeslotProvider?.lastName}
                    </div>
                </div>
                <div className={s.tsBody}>
                    {Object.entries(timeslotGroups).map(([dateKey, slots]) => {
                        const d = scenarioTz ? dayjs(dateKey).tz(scenarioTz) : dayjs(dateKey);
                        return (
                            <div key={dateKey}>
                                <div className={s.tsDateGroup}>{d.format('ddd, MMM D').toUpperCase()}</div>
                                {slots.map((slot, idx) => {
                                    const isSelected = pendingSlot === slot;
                                    const label = formatSlotTime(slot, scenarioTz);
                                    return (
                                        <div
                                            key={idx}
                                            className={`${s.tsSlot} ${isSelected ? s.tsSlotSelected : ''}`}
                                            onClick={() => setPendingSlot(slot)}
                                        >
                                            <div className={s.tsRadio} />
                                            <span className={s.tsLabel}>{label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                <div className={s.tsFooter}>
                    <button type="button" className={s.tsCancelBtn} onClick={() => setTimeslotProvider(null)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={s.tsConfirmBtn}
                        disabled={!pendingSlot}
                        onClick={handleConfirmTimeslot}
                    >
                        Confirm time
                    </button>
                </div>
            </Modal>

            <Modal
                title={null}
                open={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                footer={null}
                width={480}
                destroyOnClose
                className={s.confirmModal}
                zIndex={1020}
            >
                <div className={s.confirmHeader}>
                    <h3 className={s.confirmTitle}>Confirm Reassignment</h3>
                </div>
                <div className={s.confirmBody}>
                    <p className={s.confirmDesc}>
                        {patientName ? (
                            <>
                                Reassign <strong>{patientName}</strong>&apos;s{' '}
                            </>
                        ) : (
                            'Reassign '
                        )}
                        <strong>{appointmentTitle}</strong>
                        {(() => {
                            const slot = selectedSlot || defaultSlot;
                            if (!slot?.startAt) return '';
                            const start = scenarioTz ? dayjs(slot.startAt).tz(scenarioTz) : dayjs(slot.startAt);
                            return (
                                <>
                                    {' '}
                                    on{' '}
                                    <strong>
                                        {start.format('ddd, MMM D')} at {start.format('h:mm A')}
                                    </strong>
                                </>
                            );
                        })()}
                        {currentProviderName && (
                            <>
                                {' '}
                                from <strong>{currentProviderName}</strong>
                            </>
                        )}
                        {selectedProvider && (
                            <>
                                {' '}
                                to{' '}
                                <strong>
                                    {selectedProvider.firstName} {selectedProvider.lastName}
                                    {selectedProvider.credential ? `, ${selectedProvider.credential}` : ''}
                                </strong>
                            </>
                        )}
                        ?
                    </p>

                    {showPriceSection && (
                        <div className={s.priceHandling}>
                            <div className={s.priceHandlingHeader}>
                                <svg
                                    className={s.priceHandlingIcon}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                <span className={s.priceHandlingText}>
                                    {selectedProvider?.firstName}&apos;s price is different from the original. Which
                                    price should the patient be charged when the visit is completed?
                                </span>
                            </div>
                            <div className={s.priceOptions}>
                                <div
                                    className={`${s.priceOption} ${useNewPrice ? s.priceOptionSelected : ''}`}
                                    onClick={() => setUseNewPrice(true)}
                                >
                                    <div className={s.priceOptionRadio} />
                                    <div>
                                        <div className={s.priceOptionTitle}>
                                            Use the new provider&apos;s price ($
                                            {Math.round((selectedProvider?.newServicePriceCents || 0) / 100)})
                                        </div>
                                        <div className={s.priceOptionSub}>
                                            The patient will be charged $
                                            {Math.round((selectedProvider?.newServicePriceCents || 0) / 100)} after the
                                            visit is completed.
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={`${s.priceOption} ${!useNewPrice ? s.priceOptionSelected : ''}`}
                                    onClick={() => setUseNewPrice(false)}
                                >
                                    <div className={s.priceOptionRadio} />
                                    <div>
                                        <div className={s.priceOptionTitle}>
                                            Keep the original price ($
                                            {Math.round((scenarioData?.originalServicePriceCents || 0) / 100)})
                                        </div>
                                        <div className={s.priceOptionSub}>
                                            The patient will be charged $
                                            {Math.round((scenarioData?.originalServicePriceCents || 0) / 100)} after the
                                            visit is completed.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={s.confirmNotify}>
                        <Checkbox checked={notifyPatient} onChange={(e) => setNotifyPatient(e.target.checked)}>
                            Notify patient
                        </Checkbox>
                        <Checkbox checked={notifyNewProvider} onChange={(e) => setNotifyNewProvider(e.target.checked)}>
                            Notify{' '}
                            {selectedProvider
                                ? `${selectedProvider.firstName} ${selectedProvider.lastName}`
                                : 'new provider'}{' '}
                            (new provider)
                        </Checkbox>
                        <Checkbox
                            checked={notifyOriginalProvider}
                            onChange={(e) => setNotifyOriginalProvider(e.target.checked)}
                        >
                            Notify {currentProviderName || 'original provider'} (original provider)
                        </Checkbox>
                    </div>
                </div>
                <div className={s.confirmFooter}>
                    <button type="button" className={s.confirmCancelBtn} onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={s.confirmReassignBtn}
                        disabled={submitting}
                        onClick={handleReassign}
                    >
                        {submitting ? 'Reassigning…' : 'Reassign'}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default ReassignAppointmentModal;
